import React, { useEffect, useState } from 'react'
import GlobalImports from '../shared/GlobalImports'
import moment from 'moment'
import { DataCard, DateBasedChart, EmptyGrid, GlobalLoader, XYChart } from '@reactiveonline/reactive_admin_shared-packages'
import { truncateString } from '@reactiveonline/reactive_admin_shared-packages/utils'
import { ReportsIndex } from '@reactiveonline/reactive_admin_shared-packages/reports'
import PresetDateRangePicker from '../shared/PresetDateRangePicker'

export default function DashboardIndex({
  appProps, accountsList, accountsThisMonth, accountsToday, canViewQuickData, chartColors, fetchDashboardInfoPath, icon, monthRevenue, reports
}) {
  const [loading, setLoading] = useState(true)
  const [salesOverTimeData, setSalesOverTimeData] = useState([])
  const [salesByPaymentMethodData, setSalesByPaymentMethodData] = useState([])
  const [salesByFlavorData, setSalesByFlavorData] = useState([])
  const [salesByChannelData, setSalesByChannelData] = useState([])
  const [cashRegisterSalesData, setCashRegisterSalesData] = useState([])
  const [taxDocumentsOverTime, setTaxDocumentsOverTime] = useState([])

  const [dateRange, setDateRange] = useState({
    start_date: moment().subtract(30, 'days'),
    end_date: moment(new Date())
  })

  useEffect(() => {
    fetchData()
  }, [])

  function prepareDataForBarChart(data, xAxis, yAxis){
    let xValues = []
    const finalData = data.map(item => {
      let xValue = truncateString(item[xAxis], 20)
      while (xValues.includes(xValue)){
        xValue = xValue + " "
      }
      xValues.push(xValue)
      return({
        category: xValue,
        [yAxis]: item[yAxis]
      })
    })
    return finalData
  }

  function fetchData(range = dateRange) {
    setLoading(true)

    Rails.ajax({
      type: 'GET',
      url: `${ fetchDashboardInfoPath }${ '?date_range=' + JSON.stringify(range) }`,
      dataType: 'json',
      success: res => {
        setSalesOverTimeData(res.salesOverTimeData.map(item => (
          {
            date: item.date,
            value: item.revenue
          }
        )))

        setSalesByPaymentMethodData(prepareDataForBarChart(res.salesByPaymentMethodData, "method_name", "revenue"))
        setSalesByFlavorData(prepareDataForBarChart(res.salesByFlavorData, "flavor", "revenue"))
        setSalesByChannelData(prepareDataForBarChart(res.salesByChannelData, "channel", "revenue"))

        setCashRegisterSalesData(res.cashRegisterSalesData.map(item => (
          {
            date: item.date,
            value: item.revenue
          }
        )))

        setTaxDocumentsOverTime(res.taxDocumentsOverTime.map(item => (
          {
            date: item.date,
            value: item.count
          }
        )))

        setLoading(false)
      }
    })
  }

  function handleApplyDateRange(updatedDateRange) {
    setDateRange(updatedDateRange)
    fetchData(updatedDateRange)
  }

  return (
    <>
      <div className='page-wrapper-header flex-box content-space-between'>
        <div className='page-header'>
          <i className={ `fa-light ${ icon }` } />
          <h1 className='page-header-title'>
            { Support.translations.reports.dashboard }
          </h1>
        </div>

        <div className='flex-box items-center'>
          <PresetDateRangePicker
            value={ dateRange }
            onApply={ handleApplyDateRange }
            maxDateRangeDays={ 92 }
          />
        </div>
      </div>

      { canViewQuickData &&
        <>
          <div className='card'>
            <div className='card-content'>
              <div className='card-title'>
                { Support.translations.home.quick_data }
              </div>
            </div>
          </div>

          <div className='flex-box flex-wrap' style={{ gap: 20 }}>
            <div className='flex-1'>
              <div className='quick-data-card-wrapper'>
                <DataCard
                  icon={ 'fa-calendar-day' }
                  title={ Support.translations.home.registrations_today }
                  data={ accountsToday }
                />
              </div>
            </div>

            <div className='flex-1'>
              <div className='quick-data-card-wrapper'>
                <DataCard
                  icon={ 'fa-calendar-week' }
                  title={ Support.translations.home.registrations_this_month }
                  data={ accountsThisMonth }
                />
              </div>
            </div>

            <div className='flex-1'>
              <div className='quick-data-card-wrapper'>
                <DataCard
                  icon={ 'fa-money-bill-trend-up' }
                  title={ Support.translations.home.month_revenue }
                  data={ monthRevenue }
                />
              </div>
            </div>
          </div>
        </>
      }

      <div className='card'>
        <div className='card-content'>
          <div className='card-title'>
            { Support.translations.home.data_per_flavour }
          </div>
        </div>
      </div>

      <div className='flex-box flex-wrap' style={{ gap: 20 }}>
        { accountsList.map(item => (
          <div
            key={ item.application_name }
            className='flex-1'
          >
            <div className='quick-data-card-wrapper'>
              <DataCard
                icon={ item.icon }
                title={ item.application_name }
                data={ item.number_of_accounts }
              />
            </div>
          </div>
        ))}
      </div>

      <div className='flex-box flex-wrap items-stretch dashboard-wrapper'>
        <div className='dashboard-item-wrapper'>
          <div className='card' style={{ height: '100%' }}>
            <div className='card-content' style={{ height: '100%' }}>
              <div className="card-title">
                <div className='flex-box items-center'>
                  { Support.translations.reports.total_sales }
                </div>
              </div>
              <div className='card-body' style={{ height: '100%' }}>
                { salesOverTimeData.length > 0 ?
                  <DateBasedChart
                    data={ salesOverTimeData }
                    chartKey={ 'chartdiv1' }
                  />
                  :
                  <div
                    className='flex-box items-center content-center'
                    style={{ height: '100%' }}
                  >
                    <EmptyGrid
                      noResourcesTitle={ ReactiveCore.translations.analytics.noData }
                      iconName={ 'fa-chart-scatter-bubble' }
                      disableReload
                    />
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <div className='dashboard-item-wrapper'>
          <div className='card' style={{ height: '100%' }}>
            <div className='card-content' style={{ height: '100%' }}>
              <div className="card-title">
                <div className='flex-box items-center'>
                  { Support.translations.reports.sales_by_payment_method }
                </div>
              </div>
              <div className='card-body' style={{ height: '100%' }}>
                { salesByPaymentMethodData.length > 0 ?
                  <XYChart
                    data={ salesByPaymentMethodData }
                    seriesLabels={ [{ revenue: ReactiveCore.translations.columns.gross_sales }] }
                    xAxisTitle={ '' }
                    yAxisTitle={ '' }
                    columnWidth={ 15 }
                    chartColors={ chartColors }
                    chartKey={ "chartdiv2" }
                  />
                  :
                  <div
                    className='flex-box items-center content-center'
                    style={{ height: '100%' }}
                  >
                    <EmptyGrid
                      noResourcesTitle={ ReactiveCore.translations.analytics.noData }
                      iconName={ 'fa-chart-scatter-bubble' }
                      disableReload
                    />
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <div className='dashboard-item-wrapper'>
          <div className='card' style={{ height: '100%' }}>
            <div className='card-content' style={{ height: '100%' }}>
              <div className="card-title">
                <div className='flex-box items-center'>
                  { Support.translations.reports.sales_by_flavor }
                </div>
              </div>
              <div className='card-body' style={{ height: '100%' }}>
                { salesByFlavorData.length > 0 ?
                  <XYChart
                    data={ salesByFlavorData }
                    seriesLabels={ [{ revenue: ReactiveCore.translations.columns.gross_sales }] }
                    xAxisTitle={ '' }
                    yAxisTitle={ '' }
                    columnWidth={ 15 }
                    chartColors={ chartColors }
                    chartKey={ "chartdiv3" }
                  />
                  :
                  <div
                    className='flex-box items-center content-center'
                    style={{ height: '100%' }}
                  >
                    <EmptyGrid
                      noResourcesTitle={ ReactiveCore.translations.analytics.noData }
                      iconName={ 'fa-chart-scatter-bubble' }
                      disableReload
                    />
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <div className='dashboard-item-wrapper'>
          <div className='card' style={{ height: '100%' }}>
            <div className='card-content' style={{ height: '100%' }}>
              <div className="card-title">
                <div className='flex-box items-center'>
                  { Support.translations.reports.sales_by_channel }
                </div>
              </div>
              <div className='card-body' style={{ height: '100%' }}>
                { salesByChannelData.length > 0 ?
                  <XYChart
                    data={ salesByChannelData }
                    seriesLabels={ [{ revenue: ReactiveCore.translations.columns.gross_sales }] }
                    xAxisTitle={ '' }
                    yAxisTitle={ '' }
                    columnWidth={ 15 }
                    chartColors={ chartColors }
                    chartKey={ "chartdiv4" }
                  />
                  :
                  <div
                    className='flex-box items-center content-center'
                    style={{ height: '100%' }}
                  >
                    <EmptyGrid
                      noResourcesTitle={ ReactiveCore.translations.analytics.noData }
                      iconName={ 'fa-chart-scatter-bubble' }
                      disableReload
                    />
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <div className='dashboard-item-wrapper'>
          <div className='card' style={{ height: '100%' }}>
            <div className='card-content' style={{ height: '100%' }}>
              <div className="card-title">
                <div className='flex-box items-center'>
                  { Support.translations.reports.cash_register_total_sales }
                </div>
              </div>
              <div className='card-body' style={{ height: '100%' }}>
                { cashRegisterSalesData.length > 0 ?
                  <DateBasedChart
                    data={ cashRegisterSalesData }
                    chartKey={ 'chartdiv5' }
                  />
                  :
                  <div
                    className='flex-box items-center content-center'
                    style={{ height: '100%' }}
                  >
                    <EmptyGrid
                      noResourcesTitle={ ReactiveCore.translations.analytics.noData }
                      iconName={ 'fa-chart-scatter-bubble' }
                      disableReload
                    />
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <div className='dashboard-item-wrapper'>
          <div className='card' style={{ height: '100%' }}>
            <div className='card-content' style={{ height: '100%' }}>
              <div className="card-title">
                <div className='flex-box items-center'>
                  { Support.translations.reports.tax_docs_over_time }
                </div>
              </div>
              <div className='card-body' style={{ height: '100%' }}>
                { taxDocumentsOverTime.length > 0 ?
                  <DateBasedChart
                    data={ taxDocumentsOverTime }
                    chartKey={ 'chartdiv6' }
                  />
                  :
                  <div
                    className='flex-box items-center content-center'
                    style={{ height: '100%' }}
                  >
                    <EmptyGrid
                      noResourcesTitle={ ReactiveCore.translations.analytics.noData }
                      iconName={ 'fa-chart-scatter-bubble' }
                      disableReload
                    />
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

      </div>

      { !loading &&
        <ReportsIndex
          reports={ reports }
        />
      }

      { loading && <GlobalLoader fullPage /> }

      <GlobalImports appProps={ appProps } />
    </>
  )
}
